import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MessageSquare, Send, Loader2, Sparkles, FileText, User, BookMarked, Trash2, Pin, Plus, Pencil, History, Search, ScrollText } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/TenantContext";
import { Card } from "@/components/ui/card";
import { PageHero } from "@/components/PageHero";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Markdown } from "@/components/Markdown";

const SUGGESTIONS = [
  "Quali procedure riguardano il processo Acquisti?",
  "Quali documenti coprono il punto norma 7.5?",
  "Elenca i documenti obsoleti o in bozza.",
  "Riassumi lo scopo della PG-07.",
];

const fmtTime = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
};

export default function Chat() {
  const { user } = useAuth();
  const { tenantId, currentOrg } = useTenant();
  const isConsultant = user?.role === "consultant";
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState([]);
  const [notesOpen, setNotesOpen] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSummary, setActiveSummary] = useState("");
  const scrollRef = useRef(null);

  const needTenant = isConsultant && !tenantId;

  const loadNotes = () => {
    if (!tenantId) { setNotes([]); return; }
    api.get(`/knowledge?tenant_id=${tenantId}`).then((r) => setNotes(r.data)).catch(() => setNotes([]));
  };

  const loadConversations = (q = searchQuery) => {
    if (!tenantId) { setConversations([]); return; }
    const qs = q && q.trim() ? `&q=${encodeURIComponent(q.trim())}` : "";
    api.get(`/conversations?tenant_id=${tenantId}${qs}`).then((r) => setConversations(r.data)).catch(() => setConversations([]));
  };

  useEffect(() => { setMessages([]); setActiveId(null); setActiveSummary(""); setSearchQuery(""); loadNotes(); loadConversations(""); }, [tenantId]);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages, loading]);

  const addNote = async () => {
    if (!newNote.trim() || !tenantId) return;
    try {
      await api.post("/knowledge", { tenant_id: tenantId, text: newNote.trim() });
      setNewNote(""); loadNotes(); toast.success("Informazione di sistema aggiunta");
    } catch (e) { toast.error(apiError(e)); }
  };
  const delNote = async (id) => {
    try { await api.delete(`/knowledge/${id}`); loadNotes(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const newConversation = () => { setMessages([]); setActiveId(null); setActiveSummary(""); setInput(""); };

  const openConversation = async (id) => {
    if (loading) return;
    try {
      const r = await api.get(`/conversations/${id}`);
      const msgs = (r.data.messages || []).map((m) => ({ role: m.role, content: m.content, citations: m.citations || [] }));
      setMessages(msgs); setActiveId(id); setActiveSummary(r.data.summary || "");
    } catch (e) { toast.error(apiError(e)); }
  };

  const renameConversation = async (c) => {
    const title = window.prompt("Rinomina conversazione", c.title || "");
    if (title === null) return;
    try { await api.patch(`/conversations/${c.id}`, { title: title.trim() || "Conversazione" }); loadConversations(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const deleteConversation = async (c) => {
    if (!window.confirm(`Eliminare la conversazione "${c.title}"?`)) return;
    try {
      await api.delete(`/conversations/${c.id}`);
      if (activeId === c.id) newConversation();
      loadConversations();
    } catch (e) { toast.error(apiError(e)); }
  };

  const ask = async (question) => {
    if (!question.trim() || loading) return;
    if (needTenant) { toast.error("Seleziona un cliente in alto"); return; }
    const history = messages.map((m) => ({ role: m.role, content: m.content }));
    setMessages((m) => [...m, { role: "user", content: question }]);
    setInput("");
    setLoading(true);
    try {
      const r = await api.post("/chat", { tenant_id: tenantId || undefined, question, history, conversation_id: activeId || undefined });
      setMessages((m) => [...m, { role: "assistant", content: r.data.answer, citations: r.data.citations || [] }]);
      const cid = r.data.conversation_id;
      if (cid) {
        setActiveId(cid);
        api.get(`/conversations/${cid}`).then((rr) => setActiveSummary(rr.data.summary || "")).catch(() => {});
      }
      loadConversations();
      if (r.data.saved_note) {
        toast.success("Registrata come informazione di sistema", { description: r.data.saved_note, icon: <Pin className="h-4 w-4" /> });
        loadNotes();
      }
    } catch (e) {
      toast.error(apiError(e));
      setMessages((m) => [...m, { role: "assistant", content: "Errore nel generare la risposta.", citations: [] }]);
    } finally { setLoading(false); }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-8.5rem)] lg:h-[calc(100vh-8rem)]">
      <div className="mb-4 flex flex-col md:flex-row md:items-start justify-between gap-3 md:gap-4">
        <div className="flex-1 min-w-0">
        <PageHero label="Chiedi a Steve" title="Chiedi a Steve"
          subtitle={<>Chiedi a Steve, in linguaggio naturale, tutto sui documenti di {tenantId ? <strong>{currentOrg?.nome}</strong> : "un cliente"} — risponde citando le fonti.<span className="inline-flex items-center gap-1 ml-2 text-blue-700"><Sparkles className="h-3 w-3" /> ricerca semantica sui contenuti</span></>} />
        </div>
        {isConsultant && tenantId && (
          <Button variant="outline" size="sm" onClick={() => setNotesOpen(true)} data-testid="open-knowledge-btn"
            className="shrink-0 border-indigo-300 text-indigo-700 hover:bg-indigo-50 mt-1">
            <BookMarked className="h-4 w-4 mr-2" /> Informazioni di sistema
            {notes.length > 0 && <Badge className="ml-2 bg-indigo-600 text-white">{notes.length}</Badge>}
          </Button>
        )}
      </div>

      <div className="flex-1 flex gap-4 min-h-0">
        {!needTenant && (
          <Card className="hidden md:flex w-64 lg:w-72 shrink-0 flex-col border-slate-200 overflow-hidden" data-testid="conversations-panel">
            <div className="p-3 border-b border-slate-200">
              <Button onClick={newConversation} data-testid="new-conversation-btn"
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 text-white">
                <Plus className="h-4 w-4 mr-2" /> Nuova conversazione
              </Button>
              <div className="relative mt-2.5">
                <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                <Input value={searchQuery} data-testid="conversation-search"
                  onChange={(e) => { setSearchQuery(e.target.value); loadConversations(e.target.value); }}
                  placeholder="Cerca nelle conversazioni..." className="pl-8 h-9 text-sm" />
              </div>
            </div>
            <div className="flex items-center gap-1.5 px-3 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              <History className="h-3.5 w-3.5" /> Cronologia
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-1" data-testid="conversations-list">
              {conversations.length === 0 ? (
                <p className="text-xs text-slate-400 px-2 py-4 text-center">{searchQuery ? "Nessun risultato per la ricerca." : "Nessuna conversazione salvata. Inizia a chiedere a Steve."}</p>
              ) : conversations.map((c) => (
                <div key={c.id} onClick={() => openConversation(c.id)} data-testid={`conversation-item-${c.id}`}
                  className={`group rounded-lg px-3 py-2 cursor-pointer transition-colors ${activeId === c.id ? "bg-blue-50 border border-blue-200" : "hover:bg-slate-100 border border-transparent"}`}>
                  <div className="flex items-start justify-between gap-1.5">
                    <p className="text-sm font-medium text-slate-800 leading-snug line-clamp-2 flex-1">{c.title}</p>
                    <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                      <button onClick={(e) => { e.stopPropagation(); renameConversation(c); }} data-testid={`conversation-rename-${c.id}`}
                        className="text-slate-400 hover:text-blue-600 p-0.5" title="Rinomina"><Pencil className="h-3.5 w-3.5" /></button>
                      <button onClick={(e) => { e.stopPropagation(); deleteConversation(c); }} data-testid={`conversation-delete-${c.id}`}
                        className="text-slate-400 hover:text-red-600 p-0.5" title="Elimina"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-0.5">{fmtTime(c.updated_at)} · {c.message_count} messaggi</p>
                </div>
              ))}
            </div>
          </Card>
        )}

        <Card className="flex-1 flex flex-col border-slate-200 overflow-hidden min-w-0">
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-5" data-testid="chat-messages">
            {activeSummary && messages.length > 0 && (
              <div className="rounded-lg border border-indigo-200 bg-indigo-50/70 px-4 py-3 flex gap-2.5" data-testid="conversation-summary">
                <ScrollText className="h-4 w-4 text-indigo-600 mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-indigo-700">Riassunto della conversazione</p>
                  <p className="text-sm text-slate-700 leading-snug mt-0.5">{activeSummary}</p>
                </div>
              </div>
            )}
            {needTenant ? (
              <div className="h-full flex flex-col items-center justify-center text-center">
                <MessageSquare className="h-10 w-10 text-slate-300 mb-3" />
                <p className="text-slate-500">Seleziona un cliente dal menu in alto e Steve sarà pronto ad aiutarti.</p>
              </div>
            ) : messages.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center">
                <div className="h-14 w-14 rounded-full bg-gradient-to-br from-blue-600 to-indigo-500 flex items-center justify-center mb-4 shadow-lg shadow-blue-600/20">
                  <Sparkles className="h-7 w-7 text-white" />
                </div>
                <p className="text-slate-800 font-semibold text-lg">Ciao, sono Steve 👋</p>
                <p className="text-slate-500 mt-1 max-w-md">Il tuo assistente per i documenti di questo cliente. Chiedimi pure quello che ti serve: rispondo citando sempre le fonti.</p>
                <div className="flex flex-wrap gap-2 justify-center mt-5 max-w-lg">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} onClick={() => ask(s)} data-testid="chat-suggestion"
                      className="text-xs px-3 py-2 rounded-full border border-slate-200 text-slate-600 hover:border-blue-400 hover:text-blue-700 transition-colors">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((m, i) => (
                <div key={i} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`} data-testid={`chat-msg-${m.role}`}>
                  {m.role === "assistant" && (
                    <div className="h-8 w-8 rounded-full bg-blue-700 flex items-center justify-center shrink-0">
                      <Sparkles className="h-4 w-4 text-white" />
                    </div>
                  )}
                  <div className={`max-w-[75%] ${m.role === "user" ? "order-1" : ""}`}>
                    <div className={`rounded-lg px-4 py-3 text-sm leading-relaxed ${
                      m.role === "user" ? "bg-blue-700 text-white whitespace-pre-wrap" : "bg-slate-100 text-slate-800"
                    }`}>
                      {m.role === "assistant" ? <Markdown className="text-slate-800">{m.content}</Markdown> : m.content}
                    </div>
                    {m.citations?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2" data-testid="chat-citations">
                        {m.citations.map((c) => (
                          <Link key={c.id} to={`/documenti/${c.id}`}>
                            <Badge variant="outline" className="gap-1 hover:border-blue-400 hover:bg-blue-50 cursor-pointer font-mono text-[11px]">
                              <FileText className="h-3 w-3" /> {c.codice}
                            </Badge>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                  {m.role === "user" && (
                    <div className="h-8 w-8 rounded-full bg-slate-300 flex items-center justify-center shrink-0">
                      <User className="h-4 w-4 text-slate-600" />
                    </div>
                  )}
                </div>
              ))
            )}
            {loading && (
              <div className="flex gap-3">
                <div className="h-8 w-8 rounded-full bg-blue-700 flex items-center justify-center shrink-0">
                  <Sparkles className="h-4 w-4 text-white" />
                </div>
                <div className="bg-slate-100 rounded-lg px-4 py-3">
                  <Loader2 className="h-4 w-4 animate-spin text-slate-500" />
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-slate-200 p-4">
            <form onSubmit={(e) => { e.preventDefault(); ask(input); }} className="flex gap-2">
              <Input value={input} onChange={(e) => setInput(e.target.value)} disabled={needTenant || loading}
                placeholder="Scrivi a Steve la tua domanda..." data-testid="chat-input" />
              <Button type="submit" disabled={needTenant || loading || !input.trim()} data-testid="chat-send"
                className="bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 text-white shrink-0">
                <Send className="h-4 w-4" />
              </Button>
            </form>
            <p className="text-[11px] text-slate-500 mt-2">Le risposte si basano solo sui documenti archiviati. Verifica sempre citando le fonti.</p>
          </div>
        </Card>
      </div>

      <Dialog open={notesOpen} onOpenChange={setNotesOpen}>
        <DialogContent className="max-w-lg" data-testid="knowledge-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <BookMarked className="h-4 w-4 text-indigo-600" /> Informazioni di sistema · {currentOrg?.nome}
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500 -mt-1">
            Correzioni e fatti confermati che Steve usa come fonte prioritaria in chat, verifica copertura e coerenza. Vengono registrate automaticamente quando correggi Steve in chat; puoi anche aggiungerle o rimuoverle qui.
          </p>
          <div className="flex gap-2">
            <Input value={newNote} onChange={(e) => setNewNote(e.target.value)} data-testid="knowledge-input"
              placeholder="Es. Il Riesame della Direzione si tiene a marzo."
              onKeyDown={(e) => e.key === "Enter" && addNote()} />
            <Button onClick={addNote} data-testid="knowledge-add-btn"
              className="bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 text-white shrink-0">
              Aggiungi
            </Button>
          </div>
          <div className="max-h-72 overflow-y-auto space-y-2 pt-1" data-testid="knowledge-list">
            {notes.length === 0 ? (
              <p className="text-sm text-slate-400 py-6 text-center">Nessuna informazione di sistema registrata.</p>
            ) : notes.map((n) => (
              <div key={n.id} className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                <Pin className="h-3.5 w-3.5 text-indigo-500 mt-0.5 shrink-0" />
                <span className="text-sm text-slate-700 flex-1">{n.text}</span>
                <button onClick={() => delNote(n.id)} data-testid={`knowledge-del-${n.id}`}
                  className="text-slate-400 hover:text-red-600 shrink-0" title="Elimina">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
